import React, { useEffect, useState } from 'react';
import 'primeicons/primeicons.css';
import { money } from './helpers';
import { ModalPortal } from './ModalPortal';

/* ═══════════════════════════════════════════════════════════════════════
   DATOS DE PAGO — oficiales, confirmados por el usuario el 30-jul-2026.
   Los mismos van FIJOS en las plantillas UTILITY de WhatsApp (ver §6 de
   plan-whatsapp-cobranza.md), así que cambiar uno obliga a re-aprobar 3
   plantillas en Meta. No los edites sin avisar.
   PENDIENTE: `contacto` sigue siendo el de ejemplo — falta el real.
   ═══════════════════════════════════════════════════════════════════════ */
export const DATOS_PAGO = {
  banco: 'BBVA',
  cuenta: '0121006193',
  clabe: '012180001210061937',
  rfc: 'CCA2308224G3',
  contacto: '55 1234 5678', // ⚠️ de ejemplo, reemplazar por el real
  correoCobranza: 'cobranza@peicar.com.mx',
};

const RENTA_LBL = { AP: 'renta', AF: 'pago mensual', CS: 'pago mensual' };

/* Bloque de datos bancarios, idéntico en todos los mensajes. Se mantiene alineado
   con las plantillas UTILITY de WhatsApp (§6 de plan-whatsapp-cobranza.md). */
const LINEA_PAGO = () => {
  const { banco, cuenta, clabe, rfc } = DATOS_PAGO;
  return `Banco: ${banco} · CLABE: ${clabe} · Cuenta: ${cuenta} · RFC: ${rfc}`;
};

/**
 * Texto pre-armado por tipo de plantilla.
 * ctx = { nombre, clave, prod, veh, renta, dia, atraso, vencido, mora, liquidacion }
 */
export const buildMensaje = (tipo, ctx = {}) => {
  const {
    nombre = 'cliente', clave = '', prod = 'AP', veh = 'tu unidad',
    renta = 0, dia = '', atraso = 0, vencido = 0, mora = 0, liquidacion = null,
  } = ctx;
  const rentaLbl = RENTA_LBL[prod] || 'pago mensual';
  const { contacto } = DATOS_PAGO;

  if (tipo === 'recordatorio') {
    return `Hola ${nombre}, te recordamos que tu ${rentaLbl} de ${money(renta)} de tu ${veh} vence el día ${dia} de este mes. Puedes pagar por transferencia SPEI o depósito. ${LINEA_PAGO()}. Cualquier duda estamos a tus órdenes. — PEICAR`;
  }
  if (tipo === 'atraso') {
    const moraTxt = mora ? ` (+ Moratorios: ${money(mora)})` : '';
    return `Hola ${nombre}, tu contrato ${clave} presenta un saldo vencido de ${money(vencido)}${moraTxt} con ${atraso} días de atraso. Te pedimos regularizarlo lo antes posible para evitar cargos adicionales. Puedes pagar por transferencia SPEI o depósito. ${LINEA_PAGO()}. Contacto: ${contacto}. — PEICAR`;
  }
  if (tipo === 'estado') {
    const liq = liquidacion != null ? ` Liquidación anticipada: ${money(liquidacion)}.` : '';
    return `Estado de cuenta PEICAR — ${clave}.${liq} Para cualquier aclaración estamos a tus órdenes. Contacto: ${contacto}. — PEICAR`;
  }
  // cotizacion (liquidación anticipada)
  const total = liquidacion != null ? money(liquidacion) : '(saldo capital + intereses devengados)';
  return `Hola ${nombre}, aquí está tu cotización de liquidación anticipada de tu contrato ${clave}: saldo capital más intereses devengados al corte. Total a pagar: ${total}. Esta cotización es válida por 5 días hábiles. — PEICAR`;
};

const TITULOS = {
  recordatorio: 'Recordatorio de pago',
  atraso: 'Aviso de atraso',
  estado: 'Estado de cuenta',
  cotizacion: 'Cotización de liquidación',
};
const ICONOS = {
  recordatorio: { ic: 'pi-bell', c: '#2BA7CE', b: '#E4F5FB' },
  atraso: { ic: 'pi-exclamation-triangle', c: '#E5631B', b: '#FCEADF' },
  estado: { ic: 'pi-file', c: '#21588C', b: '#E8EFF6' },
  cotizacion: { ic: 'pi-wallet', c: '#00BA6E', b: '#E5F8EF' },
};

// Normaliza un teléfono a formato E.164 México para wa.me (52 + 10 dígitos).
// Acepta 10 dígitos, con lada 52 / +52 / 00 52, o el viejo 1 intermedio.
const normalizarMx = (raw) => {
  let n = String(raw || '').replace(/\D/g, '');
  if (!n) return '';
  if (n.startsWith('00')) n = n.slice(2);
  if (n.length === 10) return '52' + n;
  if (n.length === 12 && n.startsWith('52')) return n;
  if (n.length === 13 && n.startsWith('521')) return n;
  if (n.length === 11 && n.startsWith('1')) return '52' + n.slice(1);
  if (n.length > 10) return '52' + n.slice(-10); // último recurso: tomar los últimos 10
  return '';
};

/**
 * Modal editable para copiar / enviar por WhatsApp o Email.
 * props: { open, tipo, ctx, onClose }
 *   ctx además de lo de buildMensaje: { tel, correo }
 */
export const MensajeModal = ({ open, tipo, ctx, onClose }) => {
  const [texto, setTexto] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copiar');
  const [phone, setPhone] = useState(''); // editable: activa WhatsApp aunque no haya tel guardado

  useEffect(() => {
    if (open) {
      // Si el llamador ya armó el texto (ctx.texto), se respeta; si no, plantilla.
      setTexto(ctx?.texto || buildMensaje(tipo, ctx));
      setPhone(ctx?.tel || '');
      setCopyLabel('Copiar');
    }
  }, [open, tipo, ctx]);

  if (!open) return null;

  const waNumber = normalizarMx(phone);
  const tel = waNumber; // hay teléfono válido para WhatsApp
  const correo = (ctx?.correo || '').trim();
  const titulo = TITULOS[tipo] || 'Mensaje';
  const ico = ICONOS[tipo] || ICONOS.recordatorio;

  const doCopy = () => {
    try { navigator.clipboard.writeText(texto); } catch (e) { /* noop */ }
    setCopyLabel('¡Copiado!');
    setTimeout(() => setCopyLabel('Copiar'), 1500);
  };
  const doWa = () => { if (waNumber) window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(texto)}`, '_blank'); };
  const doEmail = () => {
    if (correo) window.open(`mailto:${correo}?subject=${encodeURIComponent('PEICAR — ' + titulo)}&body=${encodeURIComponent(texto)}`, '_blank');
  };

  const stop = (e) => e.stopPropagation();

  return (
    <ModalPortal>
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.32)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div onClick={stop} className="co-fade" style={{ background: '#fff', borderRadius: 20, width: 520, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(12,32,54,.32)' }}>
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #EEF2F5', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 13, background: ico.b, color: ico.c, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className={'pi ' + ico.ic} style={{ fontSize: 18 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>{titulo}</div>
            <div className="tnum" style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>
              Para: {ctx?.nombre || '—'} · {ctx?.clave || ''} · {ctx?.tel || 'sin teléfono'}
            </div>
          </div>
          <div onClick={onClose} className="co-row-hover" style={{ width: 32, height: 32, borderRadius: 10, background: '#F1F5F9', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <span className="pi pi-times" style={{ fontSize: 14 }} />
          </div>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            style={{ width: '100%', minHeight: 154, resize: 'vertical', border: '1.5px solid #E7EBF0', borderRadius: 14, padding: '13px 15px', fontSize: 13.5, lineHeight: 1.55, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }}
          />

          {/* Teléfono editable → activa WhatsApp aunque el contrato no tenga uno guardado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12 }}>
            <span style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>Tel. WhatsApp</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10 dígitos (MX)"
              className="tnum"
              style={{ flex: 1, border: `1.5px solid ${phone && !waNumber ? '#DC2626' : '#E7EBF0'}`, borderRadius: 10, padding: '8px 11px', fontSize: 13, color: '#0F172A', outline: 'none' }}
            />
            {phone && !waNumber && <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>inválido</span>}
          </div>

          <div style={{ display: 'flex', gap: 9, marginTop: 15 }}>
            <div onClick={doCopy} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#fff', border: '1px solid #E7EBF0', color: '#475569', fontSize: 13, fontWeight: 600, padding: '11px 16px', borderRadius: 12, cursor: 'pointer' }}>
              <span className="pi pi-copy" />{copyLabel}
            </div>
            <div onClick={doWa} title={tel ? '' : 'Escribe un número válido para activar WhatsApp'} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: tel ? '#25D366' : '#B7E9C6', color: '#fff', fontSize: 13, fontWeight: 700, padding: 11, borderRadius: 12, cursor: tel ? 'pointer' : 'not-allowed', opacity: tel ? 1 : 0.6 }}>
              <span className="pi pi-whatsapp" />WhatsApp
            </div>
            <div onClick={doEmail} title={correo ? '' : 'Sin correo registrado'} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: correo ? '#21588C' : '#B9C7D8', color: '#fff', fontSize: 13, fontWeight: 700, padding: 11, borderRadius: 12, cursor: correo ? 'pointer' : 'not-allowed', opacity: correo ? 1 : 0.6 }}>
              <span className="pi pi-envelope" />Email
            </div>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};
